import { ReactElement, Fragment, useEffect, useRef, useState } from 'react';
import { without } from 'lodash';
import {
  DragAndDropContextState,
  DragAndDropDragState,
  DragAndDropOrientation,
  DragAndDropSectionProps,
  Position,
  ReorderingDragState,
  SetDraggedOverCard,
} from './types';
import { DropPlaceholderBar } from './atoms';
import { isTruthy } from '@ardoq/common-helpers';
import { FlexBox, Grid, type GridColumns } from '@ardoq/layout';
import { DragAndDropContext } from './Context';
import {
  hasOverlayIndicator,
  isReorderingDrop,
  toDropIndicatorTarget,
} from './util';
import { GRID_GAP, GRID_MIN_COLUMN_WIDTH, WRAP_GAP } from './const';

type DragAndDropSections = ReactElement<DragAndDropSectionProps>[];

const DEFAULT_GRID_COLUMNS: GridColumns = { minWidth: GRID_MIN_COLUMN_WIDTH };

type LayoutProps =
  | { orientation: 'grid'; columns?: GridColumns }
  | {
      orientation?: Exclude<DragAndDropOrientation, 'grid'>;
      columns?: never;
    };

type DragAndDropSectionsProviderProps = LayoutProps & {
  /** Called only when the drop changes the order. */
  onDrop?: (newOrder: string[], dragAndDropState: DragAndDropDragState) => void;
  showDropIndicatorDot?: boolean;
  /**
   * Ordered ids of every card in the list. Pass this when `children` only holds
   * the rendered slice of a windowed list, so that `onDrop` reports an order
   * covering the whole list rather than the visible part of it.
   */
  order?: string[];
  children: DragAndDropSections;
};

const getNewOrder = (
  currentOrder: string[],
  { draggedCardId, draggedOverCardId, position }: ReorderingDragState
) => {
  const withoutDragged = without(currentOrder, draggedCardId);
  const index =
    withoutDragged.indexOf(draggedOverCardId) + (position === 'after' ? 1 : 0);
  const newOrder = [...withoutDragged];
  newOrder.splice(index, 0, draggedCardId);
  return newOrder;
};

type SectionsWithDropPlaceholdersProps = {
  children: DragAndDropSections;
  draggedOverCardId: string | null;
  position: Position | null;
  showDropIndicator: boolean;
  showDropIndicatorDot: boolean;
  orientation: DragAndDropOrientation;
};

const SectionsWithDropPlaceholders = ({
  children,
  draggedOverCardId,
  position,
  showDropIndicator,
  showDropIndicatorDot,
  orientation,
}: SectionsWithDropPlaceholdersProps) => (
  <>
    {children.map((child, index) => (
      <Fragment key={child.key ?? index}>
        {showDropIndicator &&
          child.props.dragAndDropCardId === draggedOverCardId &&
          position === 'before' && (
            <DropPlaceholderBar
              $orientation={orientation}
              $showDot={showDropIndicatorDot}
            />
          )}
        {child}
        {showDropIndicator &&
          child.props.dragAndDropCardId === draggedOverCardId &&
          position === 'after' && (
            <DropPlaceholderBar
              $orientation={orientation}
              $showDot={showDropIndicatorDot}
            />
          )}
      </Fragment>
    ))}
  </>
);

type DragAndDropSectionsProps = SectionsWithDropPlaceholdersProps & {
  columns: GridColumns;
};

const DragAndDropSections = ({
  orientation,
  columns,
  children,
  ...placeholderProps
}: DragAndDropSectionsProps) => {
  if (orientation === 'grid') {
    return (
      <Grid columns={columns} gap={GRID_GAP} role="presentation">
        {children}
      </Grid>
    );
  }
  if (orientation === 'wrap') {
    return (
      <FlexBox flexWrap gap={WRAP_GAP} role="presentation">
        {children}
      </FlexBox>
    );
  }
  return (
    <SectionsWithDropPlaceholders
      orientation={orientation}
      {...placeholderProps}
    >
      {children}
    </SectionsWithDropPlaceholders>
  );
};

export const DragAndDropSectionsProvider = ({
  onDrop,
  orientation = 'vertical',
  showDropIndicatorDot = true,
  order,
  columns = DEFAULT_GRID_COLUMNS,
  children,
}: DragAndDropSectionsProviderProps) => {
  const currentOrder =
    order ??
    children.map(({ props }) => props.dragAndDropCardId).filter(isTruthy);
  const [draggedCardId, setDraggedCardId] = useState<string | null>(null);
  const [draggedOverCardId, setDraggedOverCardId] = useState<string | null>(
    null
  );
  const [position, setPosition] = useState<Position | null>(null);
  const hasEndedDrag = useRef(false);

  const startDrag = (cardId: string | null) => {
    hasEndedDrag.current = false;
    setDraggedCardId(cardId);
  };

  const setDraggedOverCard: SetDraggedOverCard = (
    newDragOverCardId,
    newPosition
  ) => {
    // this is to prevent multiple dnd containers in the same page interfering with each other
    if (!draggedCardId || !currentOrder.includes(draggedCardId)) return;
    setDraggedOverCardId(newDragOverCardId);
    setPosition(newPosition);
  };

  const resetState = () => {
    setDraggedCardId(null);
    setDraggedOverCardId(null);
    setPosition(null);
  };

  const dragState: DragAndDropDragState = {
    draggedCardId,
    draggedOverCardId,
    position,
  };
  const showDropIndicator = isReorderingDrop(currentOrder, dragState);

  const contextProps: DragAndDropContextState = {
    ...dragState,
    dropIndicatorTarget:
      showDropIndicator && hasOverlayIndicator(orientation)
        ? toDropIndicatorTarget(currentOrder, dragState)
        : null,
    resetState,
    setDraggedCardId: startDrag,
    setDraggedOverCard,
    orientation,
  };

  useEffect(() => {
    // A windowed list can unmount the dragged card mid-drag, and `dragend`
    // does not reach the document from a detached node, so `drop` — which
    // fires at the card under the cursor — ends the drag too. In an ordinary
    // drag both arrive, before React has re-rendered with the reset state.
    const handleDragEnd = () => {
      if (!draggedCardId || hasEndedDrag.current) return;
      hasEndedDrag.current = true;
      resetState();
      if (!isReorderingDrop(currentOrder, dragState)) return;

      const newOrder = getNewOrder(currentOrder, dragState);
      onDrop?.(newOrder, dragState);

      children.forEach(({ props }) => {
        const { dragAndDropCardId, onOrderChange } = props;
        if (!dragAndDropCardId) return;
        const currentIndex = currentOrder.indexOf(dragAndDropCardId);
        const newIndex = newOrder.indexOf(dragAndDropCardId);
        if (currentIndex !== newIndex && onOrderChange) {
          onOrderChange(newIndex, currentIndex);
        }
      });
    };
    document.addEventListener('dragend', handleDragEnd, true);
    document.addEventListener('drop', handleDragEnd, true);
    // Disable native drop animation
    const preventDragOverEvent = (e: DragEvent) => e.preventDefault();
    document.addEventListener('dragover', preventDragOverEvent);

    return () => {
      document.removeEventListener('dragover', preventDragOverEvent);
      document.removeEventListener('drop', handleDragEnd, true);
      document.removeEventListener('dragend', handleDragEnd, true);
    };
  });

  return (
    <DragAndDropContext.Provider value={contextProps}>
      <DragAndDropSections
        draggedOverCardId={draggedOverCardId}
        position={position}
        showDropIndicator={showDropIndicator}
        showDropIndicatorDot={showDropIndicatorDot}
        orientation={orientation}
        columns={columns}
      >
        {children}
      </DragAndDropSections>
    </DragAndDropContext.Provider>
  );
};
